import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  ListingDTO,
  ListingFileDTO,
  ListingStatusDTO,
  ListingTypeDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useProvinces } from '../../hooks/useProvinceApi';
import { useListingTypes } from '../../hooks/useListingTypeApi';
import { useListingStatuses } from '../../hooks/useListingStatusApi';
import Drawer from '../common/Drawer';
import Button from '../common/Button';
import { Plus } from 'lucide-react';
import ListingFileTable from '../tables/ListingFileTable';
import ImageCarousel from '../common/ImageCarousel';
import FileUpload from '../common/FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import { confirmDelete } from '../../utils/sweetAlertUtils';
import MapboxAutocomplete from '../common/formElements/MapboxAutocomplete';
import MapboxMap from '../common/MapboxMap';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface ListingFormProps {
  initialValues: ListingDTO;
  onSubmit: (data: ListingDTO) => void;
  onCancel: () => void;
  isPublicView?: boolean;
  readonly?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  location: {
    id: 'location',
    title: 'Location',
  },
  responsiblePerson: {
    id: 'responsible-person',
    title: 'Responsible Person',
  },
  images: {
    id: 'images',
    title: 'Listing Images',
  },
  additionalInfo: {
    id: 'additional-info',
    title: 'Additional Info',
  },
  admin: {
    id: 'admin',
    title: 'Admin',
  },
} as const;

const defaultListingFile: ListingFileDTO = {
  fileId: '',
  isMain: false,
  listingId: undefined,
};

const ListingForm: React.FC<ListingFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isPublicView = false,
  readonly = false,
}) => {
  const [formData, setFormData] = useState<ListingDTO>(initialValues);
  const [listingFile, setListingFile] =
    useState<ListingFileDTO>(defaultListingFile);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [listingFilesCurrentPage, setListingFilesCurrentPage] = useState(1);
  const [listingFilesPageSize, setListingFilesPageSize] = useState(10);
  const [listingFilesTotalPages, setListingFilesTotalPages] = useState(1);
  const [listingFilesTotalRecords, setListingFilesTotalRecords] = useState(0);
  const [listingFilesIsLoading, setListingFilesIsLoading] = useState(false);
  const { uploadFile, getFileUrl } = useFileApi();
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
  const { data: listingTypesData = [], isLoading: isLoadingListingTypes } = useListingTypes();
  const { data: listingStatusesData = [], isLoading: isLoadingListingStatuses } = useListingStatuses();

  // Dropdown options computed from query data
  const statusOptions = useMemo(
    () =>
      (listingStatusesData as ListingStatusDTO[]).map((s) => ({
        value: s.id as string,
        label: (
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-full inline-block"
              style={{ backgroundColor: s.colourGrading?.colour }}
            />
            <span>{s.title}</span>
          </div>
        ),
      })),
    [listingStatusesData]
  );

  const typeOptions = useMemo(
    () =>
      listingTypesData.map((t) => ({
        value: t.id as string,
        label: t.title || '',
      })),
    [listingTypesData]
  );

  const validationRules: ValidationRules = {
    listingTypeId: [{ required: true, message: 'Listing Type is required' }],
    name: [{ required: true, message: 'Name is required' }],
    registeredName: [
      { required: true, message: 'Registered Name is required' },
    ],
    provinceId: [{ required: true, message: 'Province is required' }],
    closestCity: [{ required: true, message: 'Closest City is required' }],
    physicalAddress: [
      { required: true, message: 'Physical Address is required' },
    ],
    contactNumber: [{ required: true, message: 'Contact number is required' }],
    email: [{ required: true, message: 'Email is required' }],
    responsiblePersonName: [
      { required: true, message: 'Responsible Person Name is required' },
    ],
    responsiblePersonSurname: [
      { required: true, message: 'Responsible Person Surname is required' },
    ],
    responsiblePersonIdNumber: [
      { required: true, message: 'Responsible Person Id Number is required' },
    ],
    responsiblePersonEmail: [
      { required: true, message: 'Responsible Person Email is required' },
    ],
    responsiblePersonCellNumber: [
      { required: true, message: 'Responsible Person Cell Number is required' },
    ],
    description: [{ required: true, message: 'Description is required' }],
    listingStatusId: [
      { required: !isPublicView, message: 'Listing Status is required' },
    ],
  };

  const { errors, validate } = useFormValidation<ListingDTO>(validationRules);

  const handleFileUpload = (field: keyof ListingFileDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));
        const response = await uploadFile('files', file.name, file, 'listings');
        if (response && response.success && response.data) {
          setListingFile(prev => ({
            ...prev,
            fileId: response.data
          }));
        }
      } catch (error) {
        console.error('Error handling file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    }
  };

  const handleFileSave = () => {
    if (listingFile) {
      setFormData((prev) => {
        if (listingFile.id) {
          return {
            ...prev,
            listingFiles:
              prev.listingFiles?.map((file) => {
                if (listingFile.isMain) {
                  return {
                    ...file,
                    isMain: file.id === listingFile.id,
                  };
                }
                return file.id === listingFile.id ? listingFile : file;
              }) || [],
          };
        }

        const existingFiles = prev.listingFiles || [];

        if (listingFile.isMain) {
          return {
            ...prev,
            listingFiles: [
              ...existingFiles.map((f) => ({ ...f, isMain: false })),
              listingFile,
            ],
          };
        }

        return {
          ...prev,
          listingFiles: [...existingFiles, listingFile],
        };
      });

      handleDrawerClose();
    }
  };

  const handleFileEdit = (fileToEdit: ListingFileDTO) => {
    setListingFile(fileToEdit);
    setIsDrawerOpen(true);
  };

  const handleFileDelete = async (fileToDelete: ListingFileDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    setFormData((prev) => ({
      ...prev,
      listingFiles:
        prev.listingFiles?.filter(
          (file) => file.fileId !== fileToDelete.fileId,
        ) || [],
    }));
  };

  const handleSave = () => {
    console.log('Listing Files: ', formData.listingFiles);
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleDrawerClose = () => {
    setListingFile(defaultListingFile);
    setIsDrawerOpen(false);
  };

  const sections = isPublicView
    ? Object.values(FORM_SECTIONS).filter(s => s.id !== 'admin')
    : Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} hideSectionNav={isPublicView} formTitle="Listing Submission" hideActions={readonly}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="listingTypeId"
          label="Listing Type"
          type="dropdown"
          value={formData.listingTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Listing Type"
          required={true}
          error={errors.listingTypeId}
          options={typeOptions}
          disabled={readonly}
        />
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Name"
          required={true}
          error={errors.name}
          disabled={readonly}
        />
        <FormField
          id="registeredName"
          label="Registered Name"
          type="text"
          value={formData.registeredName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Registered Name"
          required={true}
          error={errors.registeredName}
          disabled={readonly}
        />
        <FormField
          id="websiteAddress"
          label="Website Address (include http://)"
          type="text"
          value={formData.websiteAddress || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Website Address"
          required={false}
          error={errors.websiteAddress}
          disabled={readonly}
        />
        <FormField
          id="contactNumber"
          label="Contact Number"
          type="text"
          value={formData.contactNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Contact Number"
          required={true}
          error={errors.contactNumber}
          disabled={readonly}
        />
        <FormField
          id="email"
          label="Email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Email"
          textCase='lowercase'
          required={true}
          error={errors.email}
          disabled={readonly}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.location.id}
        title={FORM_SECTIONS.location.title}
        columns={1}
      >
        <FormField
          id="provinceId"
          label="Province"
          type="dropdown"
          value={formData.provinceId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select A Province"
          required={true}
          error={errors.provinceId}
          options={(provinces || []).map((p) => ({
            value: p.id ?? '',
            label: p.title ?? '',
          }))}
          disabled={readonly}
        />
        <FormField
          id="closestCity"
          label="Closest City"
          type="text"
          value={formData.closestCity || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Closest City"
          required={true}
          error={errors.closestCity}
          disabled={readonly}
        />
        {readonly ? (
          <FormField
            id="physicalAddress"
            label="Physical Address"
            type="text"
            value={formData.physicalAddress || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Physical Address"
            required={true}
            disabled={true}
          />
        ) : (
          <MapboxAutocomplete
            fieldId="physicalAddress"
            fieldLabel="Physical Address"
            value={formData.physicalAddress || ''}
            onChange={(e) => handleChange(e, setFormData)}
            onLocationSelect={(latitude, longitude) => {
              setFormData((prev) => ({
                ...prev,
                latitude,
                longitude,
              }));
            }}
            placeholder="Enter Physical Address"
            required={true}
          />
        )}
        <MapboxMap
          latitude={formData.latitude}
          longitude={formData.longitude}
          onMarkerDragEnd={readonly ? undefined : (latitude, longitude) => {
            setFormData((prev) => ({
              ...prev,
              latitude,
              longitude,
            }));
          }}
        />
      </FormSection>

      {(!isPublicView || !readonly) && (
        <FormSection
          id={FORM_SECTIONS.responsiblePerson.id}
          title={FORM_SECTIONS.responsiblePerson.title}
        >
          <FormField
            id="responsiblePersonName"
            label="Responsible Person Name"
            type="text"
            value={formData.responsiblePersonName || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Responsible Person Name"
            required={true}
            error={errors.responsiblePersonName}
            disabled={readonly}
          />
          <FormField
            id="responsiblePersonSurname"
            label="Responsible Person Surname"
            type="text"
            value={formData.responsiblePersonSurname || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Responsible Person Surname"
            required={true}
            error={errors.responsiblePersonSurname}
            disabled={readonly}
          />
          <FormField
            id="responsiblePersonIdNumber"
            label="Responsible Person Id Number"
            type="text"
            value={formData.responsiblePersonIdNumber || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Responsible Person Id Number"
            required={true}
            error={errors.responsiblePersonIdNumber}
            disabled={readonly}
          />
          <FormField
            id="responsiblePersonEmail"
            label="Responsible Person Email"
            type="text"
            value={formData.responsiblePersonEmail || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Responsible Person Email"
            required={true}
            error={errors.responsiblePersonEmail}
            disabled={readonly}
          />
          <FormField
            id="responsiblePersonCellNumber"
            label="Responsible Person Cell Number"
            type="text"
            value={formData.responsiblePersonCellNumber || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Responsible Person Cell Number"
            required={true}
            error={errors.responsiblePersonCellNumber}
            disabled={readonly}
          />
        </FormSection>
      )}


      <FormSection
        id={FORM_SECTIONS.images.id}
        title={FORM_SECTIONS.images.title}
        columns={1}
        headerActions={
          !readonly ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsDrawerOpen(true)}
            >
              <div className="flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                Add Images
              </div>
            </Button>
          ) : null
        }
      >
        {isPublicView && readonly ? (
          <ImageCarousel
            fileIds={formData.listingFiles?.map(f => f.fileId || '') || []}
            mainFileId={formData.listingFiles?.find(f => f.isMain)?.fileId}
            autoPlay={true}
          />
        ) : (
          <ListingFileTable
            data={formData.listingFiles || []}
            handleEdit={readonly ? undefined : (dto) => handleFileEdit(dto)}
            handleDelete={readonly ? undefined : (dto) => handleFileDelete(dto)}
            currentPage={listingFilesCurrentPage}
            totalPages={listingFilesTotalPages}
            setCurrentPage={setListingFilesCurrentPage}
            pageSize={listingFilesPageSize}
            setPageSize={setListingFilesPageSize}
            isLoading={listingFilesIsLoading}
          />
        )}

        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          title={
            !formData.listingFiles?.some(
              (f) => f.fileId === listingFile.fileId,
            )
              ? 'Add new image'
              : 'Edit File'
          }
        >
          {isDrawerOpen && (
            <div className="space-y-4">
              <div className="space-y-2">
                {!formData.listingFiles?.some(
                  (f) => f.fileId === listingFile.fileId,
                ) && (
                    <FileUpload
                      id="listingFile"
                      onChange={handleFileUpload('fileId')}
                      title="Upload Listing Image"
                      accept={FileTypeConstants.ALL_SUPPORTED}
                      required={true}
                      error={errors.listingFile}
                      isUploading={uploadingFiles['fileId']}
                    />
                  )}

                <FormField
                  id="isMain"
                  label="Main Image"
                  type="checkbox"
                  value={listingFile?.isMain}
                  onChange={(e) => handleChange(e, setListingFile)}
                  placeholder="Is this the main image?"
                  required={false}
                  error={errors.isFriendly}
                  helper="Check to make main image"
                />
                <div className="flex justify-end space-x-4">
                  <Button variant="secondary" onClick={handleDrawerClose}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleFileSave}
                    disabled={!listingFile}
                  >
                    {!formData.listingFiles?.some(
                      (f) => f.fileId === listingFile.fileId,
                    )
                      ? 'Add image'
                      : 'Edit File'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Drawer>
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.additionalInfo.id}
        title={FORM_SECTIONS.additionalInfo.title}
        columns={1}
      >
        <FormField
          id="description"
          label="Description"
          type="textarea"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Description"
          required={true}
          error={errors.description}
          disabled={readonly}
        />
      </FormSection>

      {!isPublicView && (
        <FormSection
          id={FORM_SECTIONS.admin.id}
          title={FORM_SECTIONS.admin.title}
          columns={1}
        >
          <FormField
            id="listingStatusId"
            label="Listing Status"
            type="dropdown"
            value={formData.listingStatusId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Listing Status"
            required={true}
            error={errors.listingStatusId}
            options={statusOptions}
            disabled={readonly}
          />
          <FormField
            id="notes"
            label="Notes"
            type="textarea"
            value={formData.notes || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Notes"
            required={false}
            error={errors.notes}
            disabled={readonly}
          />
        </FormSection>
      )}
    </Form>
  );
};

export default ListingForm;
